import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { formatCLP, formatFecha, hoyISO, nombreMes, claveMes } from '../lib/format';
import { q } from '../lib/vinculos';
import {
  RUBROS, nombreRubro, ACEPTA_ARCHIVOS, leerNombreArchivo, subirComprobante, abrirComprobante, borrarComprobante,
} from '../lib/comprobantes';
import { useAccion } from '../lib/useAccion';
import ErrorBanner from './ErrorBanner';
import { SelectRubro } from './ComprobanteCampos';

const esArchivoValido = (f) => /^image\//.test(f.type) || f.type === 'application/pdf' || /\.(pdf|jpe?g|png|webp|heic)$/i.test(f.name);
// Documentos de identidad: se muestran pero no se marcan para importar.
const esDocumentoPersonal = (nombre) => /c[eé]dula|carnet|pasaporte/i.test(nombre);

// En una misma carpeta suele estar la boleta y el comprobante de pago del mismo gasto, con el mismo monto.
// Se deja el monto solo en uno (el que trae fecha de pago, o la foto/capture) para no contarlo dos veces.
const tieneFechaDePago = (nombre) => /\b\d{1,2}[-/]\d{1,2}\b/.test(nombre.replace(/\b20\d{2}\b/g, ''));
function marcarMontosRepetidos(filas) {
  const grupos = {};
  filas.forEach(f => {
    if (!f.monto) return;
    const carpeta = f.ruta.split('/').slice(0, -1).join('/');
    (grupos[`${carpeta}|${f.monto}`] = grupos[`${carpeta}|${f.monto}`] || []).push(f);
  });
  const sinMonto = new Map();
  Object.values(grupos).filter(g => g.length > 1).forEach(g => {
    const puntaje = (f) => (tieneFechaDePago(f.file.name) ? 2 : 0) + (/^image\//.test(f.file.type) ? 1 : 0);
    const [queda, ...resto] = [...g].sort((a, b) => puntaje(b) - puntaje(a));
    resto.forEach(f => sinMonto.set(f.key, queda.file.name));
  });
  return filas.map(f => (sinMonto.has(f.key) ? { ...f, monto: '', repetido: sinMonto.get(f.key) } : f));
}

const formVacio = () => ({ archivo: null, concepto: '', monto: '', fecha: hoyISO(), rubro: '' });

function Comprobantes({ userId }) {
  const [comprobantes, setComprobantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroRubro, setFiltroRubro] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [nuevo, setNuevo] = useState(null); // formulario "subir uno"
  const [editar, setEditar] = useState(null); // comprobante en edición
  const [importacion, setImportacion] = useState(null); // filas a importar
  const [progreso, setProgreso] = useState(null); // { hechos, total }
  const { saving, error, setError, run } = useAccion();

  const loadData = useCallback(async () => {
    if (!userId) return;
    const data = await q(supabase.from('comprobantes').select('*').eq('user_id', userId)
      .order('fecha', { ascending: false, nullsFirst: false }).order('created_at', { ascending: false }));
    setComprobantes(data || []);
  }, [userId]);

  useEffect(() => {
    loadData().catch(e => setError('Error cargando datos: ' + e.message)).finally(() => setLoading(false));
  }, [loadData, setError]);

  const abrir = (c) => abrirComprobante(c).catch(e => setError(e.message || String(e)));

  // --- Subir uno ---
  const elegirArchivo = (file) => {
    if (!file) { setNuevo({ ...nuevo, archivo: null }); return; }
    const leido = leerNombreArchivo(file.name);
    setNuevo({
      archivo: file,
      concepto: nuevo.concepto || leido.concepto,
      monto: nuevo.monto || leido.monto || '',
      fecha: leido.fecha || nuevo.fecha,
      rubro: nuevo.rubro || leido.rubro,
    });
  };

  const handleSubir = async (e) => {
    e.preventDefault();
    if (!nuevo.archivo) { setError('Elige el archivo del comprobante.'); return; }
    const ok = await run(() => subirComprobante({ userId, file: nuevo.archivo, ...sinArchivo(nuevo) }), loadData);
    if (ok) setNuevo(null);
  };

  // --- Editar ---
  const handleGuardarEdicion = async (e) => {
    e.preventDefault();
    const ok = await run(() => q(supabase.from('comprobantes').update({
      concepto: editar.concepto.trim() || 'Comprobante',
      monto: editar.monto === '' ? null : Number(editar.monto),
      fecha: editar.fecha || null,
      rubro: editar.rubro || null,
    }).eq('id', editar.id)), loadData);
    if (ok) setEditar(null);
  };

  const handleBorrar = async (c) => {
    if (!window.confirm(`¿Borrar el comprobante "${c.concepto}"? El archivo se elimina.`)) return;
    await run(() => borrarComprobante(c), loadData);
  };

  // --- Importar varios (una carpeta completa desde el PC) ---
  const prepararImportacion = (lista) => {
    const archivos = Array.from(lista || []).filter(esArchivoValido);
    if (archivos.length === 0) { setError('No encontré fotos ni PDF en lo que elegiste.'); return; }
    const yaSubidos = new Set(comprobantes.map(c => `${c.archivo_nombre}|${c.archivo_tamano}`));
    setImportacion(marcarMontosRepetidos(archivos
      .map((file, i) => {
        const ruta = file.webkitRelativePath || file.name;
        const leido = leerNombreArchivo(file.name, ruta);
        const duplicado = yaSubidos.has(`${file.name}|${file.size}`);
        const personal = esDocumentoPersonal(file.name);
        return {
          key: `${i}-${ruta}`, file, ruta, duplicado, personal,
          incluir: !duplicado && !personal,
          concepto: leido.concepto, monto: leido.monto || '', fecha: leido.fecha || '', rubro: leido.rubro,
        };
      })
      .sort((a, b) => (a.fecha || '9999').localeCompare(b.fecha || '9999') || a.ruta.localeCompare(b.ruta))));
  };

  const cambiarFila = (key, cambios) => setImportacion(filas => filas.map(f => (f.key === key ? { ...f, ...cambios } : f)));

  const handleImportar = async () => {
    const filas = importacion.filter(f => f.incluir);
    if (filas.length === 0) { setError('No hay archivos marcados para importar.'); return; }
    setProgreso({ hechos: 0, total: filas.length });
    const fallidos = [];
    for (let i = 0; i < filas.length; i++) {
      const f = filas[i];
      try {
        await subirComprobante({
          userId, file: f.file, concepto: f.concepto, monto: f.monto, fecha: f.fecha || null, rubro: f.rubro, origen: 'importado',
        });
      } catch (e) {
        fallidos.push(`${f.file.name}: ${e.message || e}`);
      }
      setProgreso({ hechos: i + 1, total: filas.length });
    }
    setProgreso(null);
    await loadData().catch(() => {});
    if (fallidos.length) {
      setError(`No se pudieron subir ${fallidos.length} archivo(s): ${fallidos.slice(0, 3).join(' · ')}`);
      setImportacion(filas => filas.filter(f => fallidos.some(x => x.startsWith(f.file.name))));
    } else {
      setImportacion(null);
    }
  };

  if (loading) return (
    <div className="bolsillos-loading"><div className="loading-spinner"></div><p>Cargando...</p></div>
  );

  // --- Lista filtrada y agrupada por mes ---
  const texto = busqueda.trim().toLowerCase();
  const visibles = comprobantes.filter(c =>
    (!filtroRubro || (c.rubro || '') === filtroRubro) &&
    (!texto || `${c.concepto} ${c.archivo_nombre || ''}`.toLowerCase().includes(texto)));
  const meses = {};
  visibles.forEach(c => { (meses[claveMes(c.fecha)] = meses[claveMes(c.fecha)] || []).push(c); });
  const clavesMes = Object.keys(meses).sort((a, b) => b.localeCompare(a));
  const totalPorRubro = (lista) => RUBROS
    .map(r => ({ ...r, total: lista.filter(c => c.rubro === r.value).reduce((s, c) => s + Number(c.monto || 0), 0) }))
    .filter(r => r.total > 0);

  const importables = importacion ? importacion.filter(f => f.incluir).length : 0;

  return (
    <div className="deudas-page">
      <div className="page-header">
        <div>
          <h2>Comprobantes</h2>
          <p className="page-subtitle">Boletas, capturas y PDF de cada pago: hogar, SII, empresa</p>
        </div>
        <button className="btn-primary" onClick={() => setNuevo(formVacio())}>+ Subir comprobante</button>
      </div>

      <ErrorBanner error={error} onClose={() => setError('')} />

      <div className="card" style={{ display: 'block', padding: 16, marginBottom: 16 }}>
        <strong>📥 Importar varios a la vez</strong>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: '4px 0 10px' }}>
          Desde el PC puedes elegir una carpeta completa (ej. "Arriendo-gastos casa" o "SII"). La app lee el concepto,
          el monto y la fecha del nombre de cada archivo; los revisas antes de guardar. No cambia tus saldos: queda como historial.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <label className="btn-secondary" style={{ cursor: 'pointer', margin: 0 }}>
            📁 Elegir carpeta
            <input type="file" multiple style={{ display: 'none' }}
              {...{ webkitdirectory: '', directory: '' }}
              onChange={e => { prepararImportacion(e.target.files); e.target.value = ''; }} />
          </label>
          <label className="btn-secondary" style={{ cursor: 'pointer', margin: 0 }}>
            🗂️ Elegir archivos
            <input type="file" multiple accept={ACEPTA_ARCHIVOS} style={{ display: 'none' }}
              onChange={e => { prepararImportacion(e.target.files); e.target.value = ''; }} />
          </label>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <input type="search" placeholder="Buscar (ej. Enel, F29)..." value={busqueda}
          onChange={e => setBusqueda(e.target.value)} style={{ flex: '2 1 180px' }} />
        <div style={{ flex: '1 1 160px' }}>
          <SelectRubro value={filtroRubro} onChange={setFiltroRubro} vacio="Todos los rubros" />
        </div>
      </div>

      {clavesMes.length === 0 ? (
        <p className="empty-text">{comprobantes.length === 0 ? 'Aún no hay comprobantes.' : 'Nada coincide con el filtro.'}</p>
      ) : clavesMes.map(clave => {
        const lista = meses[clave];
        const totales = totalPorRubro(lista);
        return (
          <div key={clave} className="card" style={{ display: 'block', padding: 16, marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
              <strong>{nombreMes(clave)}</strong>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                {totales.map(t => `${t.label}: ${formatCLP(t.total)}`).join(' · ')}
              </span>
            </div>
            {lista.map(c => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <button className="btn-icon-del small" title="Ver comprobante" style={{ fontSize: '1rem' }} onClick={() => abrir(c)}>📎</button>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.concepto}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    {c.fecha ? formatFecha(c.fecha) : 'Sin fecha'} · {nombreRubro(c.rubro)}
                    {c.movimiento_id ? ' · registrado en categorías' : c.origen === 'importado' ? ' · historial' : ''}
                  </div>
                </div>
                <strong style={{ whiteSpace: 'nowrap' }}>{c.monto != null ? formatCLP(c.monto) : '—'}</strong>
                <button className="btn-icon-del small" title="Editar" style={{ color: 'var(--primary)', fontSize: '0.9rem' }}
                  onClick={() => setEditar({ id: c.id, concepto: c.concepto, monto: c.monto ?? '', fecha: c.fecha || '', rubro: c.rubro || '' })}>✎</button>
                <button className="btn-icon-del small" title="Borrar" onClick={() => handleBorrar(c)}>×</button>
              </div>
            ))}
          </div>
        );
      })}

      {/* Modal: subir uno */}
      {nuevo && (
        <div className="modal-overlay" onClick={() => setNuevo(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Subir comprobante</h3>
              <button className="modal-close" onClick={() => setNuevo(null)}>×</button>
            </div>
            <form onSubmit={handleSubir}>
              <div className="form-group">
                <label>Archivo (foto, capture o PDF)</label>
                <input type="file" accept={ACEPTA_ARCHIVOS} onChange={e => elegirArchivo(e.target.files?.[0] || null)} required />
              </div>
              <CamposComprobante form={nuevo} setForm={setNuevo} />
              <p className="field-hint">Esto guarda el comprobante como historial. Si además quieres que descuente tu saldo, regístralo como salida en Mis Categorías o con 💸 Pagar en Gastos Fijos (ahí también puedes adjuntarlo).</p>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setNuevo(null)}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Subiendo...' : 'Guardar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: editar */}
      {editar && (
        <div className="modal-overlay" onClick={() => setEditar(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Editar comprobante</h3>
              <button className="modal-close" onClick={() => setEditar(null)}>×</button>
            </div>
            <form onSubmit={handleGuardarEdicion}>
              <CamposComprobante form={editar} setForm={setEditar} />
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setEditar(null)}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={saving}>Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: revisar importación */}
      {importacion && (
        <div className="modal-overlay" onClick={() => !progreso && setImportacion(null)}>
          <div className="modal" style={{ maxWidth: 980, width: '96vw' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Revisar {importacion.length} archivos</h3>
              {!progreso && <button className="modal-close" onClick={() => setImportacion(null)}>×</button>}
            </div>
            <p className="modal-description">
              Corrige lo que haga falta (los F29 suelen venir sin monto en el nombre). Los desmarcados no se suben.
              {importacion.some(f => f.duplicado) && ' Los que ya subiste antes vienen desmarcados.'}
              {importacion.some(f => f.personal) && ' Las cédulas u otros documentos personales vienen desmarcados.'}
            </p>
            <div style={{ maxHeight: '55vh', overflow: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
              {importacion.map(f => (
                <div key={f.key} style={{ display: 'flex', gap: 6, alignItems: 'center', padding: 6, borderBottom: '1px solid var(--border)', flexWrap: 'wrap', opacity: f.incluir ? 1 : 0.5 }}>
                  <input type="checkbox" checked={f.incluir} onChange={e => cambiarFila(f.key, { incluir: e.target.checked })}
                    style={{ width: 18, height: 18, accentColor: 'var(--primary)' }} disabled={!!progreso} />
                  <div style={{ flex: '1 1 100%', fontSize: '0.72rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', order: -1 }}>
                    {f.ruta}{f.duplicado ? ' · ya subido' : ''}{f.personal ? ' · documento personal' : ''}
                    {f.repetido ? ` · mismo monto que "${f.repetido}": se deja sin monto para no sumarlo dos veces` : ''}
                  </div>
                  <input type="text" value={f.concepto} onChange={e => cambiarFila(f.key, { concepto: e.target.value })}
                    style={{ flex: '3 1 180px' }} disabled={!!progreso} />
                  <input type="number" placeholder="Monto" value={f.monto} onChange={e => cambiarFila(f.key, { monto: e.target.value })}
                    style={{ flex: '1 1 90px', borderColor: f.incluir && !f.monto ? 'var(--warning)' : undefined }} disabled={!!progreso} />
                  <input type="date" value={f.fecha} onChange={e => cambiarFila(f.key, { fecha: e.target.value })}
                    style={{ flex: '1 1 130px', borderColor: f.incluir && !f.fecha ? 'var(--warning)' : undefined }} disabled={!!progreso} />
                  <div style={{ flex: '1 1 140px' }}>
                    <SelectRubro value={f.rubro} onChange={v => cambiarFila(f.key, { rubro: v })} />
                  </div>
                </div>
              ))}
            </div>
            <div className="modal-actions" style={{ alignItems: 'center' }}>
              <span style={{ marginRight: 'auto', fontSize: '0.85rem' }}>
                {progreso ? `Subiendo ${progreso.hechos} de ${progreso.total}...` : `${importables} marcados`}
              </span>
              {!progreso && <button type="button" className="btn-secondary" onClick={() => setImportacion(null)}>Cancelar</button>}
              <button type="button" className="btn-primary" disabled={!!progreso || importables === 0} onClick={handleImportar}>
                {progreso ? 'Subiendo...' : `Importar ${importables}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const sinArchivo = ({ archivo, ...resto }) => resto;

function CamposComprobante({ form, setForm }) {
  return (
    <>
      <div className="form-group">
        <label>Concepto</label>
        <input type="text" value={form.concepto} onChange={e => setForm({ ...form, concepto: e.target.value })}
          placeholder="ej: F29 Septiembre 2026" required />
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <div className="form-group" style={{ flex: '1 1 140px' }}>
          <label>Monto ($)</label>
          <input type="number" min="0" value={form.monto} onChange={e => setForm({ ...form, monto: e.target.value })} placeholder="0" />
        </div>
        <div className="form-group" style={{ flex: '1 1 140px' }}>
          <label>Fecha de pago</label>
          <input type="date" value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} />
        </div>
      </div>
      <div className="form-group">
        <label>Rubro</label>
        <SelectRubro value={form.rubro} onChange={v => setForm({ ...form, rubro: v })} />
      </div>
    </>
  );
}

export default Comprobantes;
